import {
  Directive,
  Inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NgControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { Subject, distinctUntilChanged, startWith, takeUntil, tap } from 'rxjs';

@Directive({
  standalone: true,
  selector: '[appCustomLabel]',
})
export class ControlValueAccesorDirective<T>
  implements ControlValueAccessor, OnChanges, OnInit {
  constructor(@Inject(Injector) private injector: Injector) { }

  @Input() type = 'text';
  @Input() minLength: number | undefined;
  @Input() additionalValidators: ValidatorFn[] = [];

  @Input() control: FormControl | undefined;
  private _isDisabled = false;
  private _destroy$ = new Subject<void>();
  private _onTouched!: () => T;

  protected getValidatorsForType(type: string): ValidatorFn[] | null {
    switch (type) {
      case 'email-required':
        return [Validators.required, Validators.email];
      case 'email':
        return [Validators.email];
      case 'number-required':
        return [
          Validators.pattern(/^[0-9]*$/),
          Validators.required,
          Validators.minLength(this.minLength || 0),
        ];
      case 'number':
        return [Validators.pattern(/^[0-9]*$/)];
      case 'number-length':
        return [
          Validators.pattern(/^[0-9]*$/),
          Validators.minLength(this.minLength || 0),
        ];

      case 'password':
        return [Validators.required, Validators.minLength(6)];
      case 'text-required':
        return [Validators.required, Validators.minLength(this.minLength || 0)];
      default:
        return [];
    }
  }
  updateValidators(): void {
    if (this.control) {
      const typeValidators = this.getValidatorsForType(this.type) || [];
      const combinedValidators = [
        ...typeValidators,
        ...this.additionalValidators,
      ];
      this.control.setValidators(combinedValidators);
      this.control.updateValueAndValidity();
    }
  }

  ngOnInit(): void {
    this.setFormControl();
    this.updateValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['type'] || changes?.['minLength']) {
      this.updateValidators();
    }
  }
  /* // ? Debug
    observeValueChanges() {
      if (this.control) {
        this.control.valueChanges.subscribe(() => {
          this.getValidatorsForType(this.type);
          console.log('Errores después de cambio:', this.control!.errors);
        });
      }
    }
  */
  setFormControl() {
    if (this.control) {
      return;
    }
    try {
      const formControl = this.injector.get(NgControl);

      switch (formControl.constructor) {
        case FormControlName:
          this.control = this.injector
            .get(FormGroupDirective)
            .getControl(formControl as FormControlName);
          break;
        default:
          this.control = (formControl as FormControlDirective)
            .form as FormControl;
          break;
      }
    } catch (err) {
      this.control = new FormControl();
    }

    // ? Debug - this.observeValueChanges();
  }

  writeValue(value: T): void {
    if (this.control && this.control.value !== value) {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (val: T | null) => T): void {
    this.control?.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        startWith(this.control.value),
        distinctUntilChanged(),
        tap((val) => fn(val))
      )
      .subscribe();
  }

  registerOnTouched(fn: () => T): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this._isDisabled !== isDisabled) {
      this._isDisabled = isDisabled;

      if (this.control) {
        if (isDisabled) {
          this.control.disable();
        } else {
          this.control.enable();
        }
      }
    }
  }
  protected isValid(): boolean {
    if (!this.control) {
      return true;
    }

    if (!this.control.touched) {
      return true;
    }

    return this.control.valid;
  }
}
